import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { PublicFrame } from '../../components/Shell'
import { ErrorNote, Spinner } from '../../components/ui'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n'

interface Target {
  kind: 'workspace' | 'partner'
  workspaceId?: string
  name: string
  role?: string
  scopes: string[]
  grantable: string[]
}
interface Consent {
  client: { name: string; redirectHost: string }
  requested: string[]
  mcpOrigin: string
  targets: Target[]
}

/**
 * MCP consent (plan, 11.2): the client's name and redirect host as the client claims them, the account to
 * connect and the scopes, capped by the person's role. Nothing here is trusted text from RefeGo.
 */
export function OAuthConsentPage() {
  const { t } = useT()
  const handle = new URLSearchParams(location.search).get('handle') ?? ''
  const q = useQuery({
    queryKey: ['oauth-consent', handle],
    queryFn: () => api<Consent>(`/api/oauth/consent?handle=${encodeURIComponent(handle)}`),
  })
  const [pick, setPick] = useState(0)
  const [off, setOff] = useState<string[]>([])
  const approve = useMutation({
    mutationFn: (target: Target) =>
      api<{ redirect: string }>('/api/oauth/consent', {
        body: {
          handle,
          target:
            target.kind === 'workspace'
              ? { kind: 'workspace', workspaceId: target.workspaceId }
              : { kind: 'partner' },
          scopes: target.scopes.filter((s) => !off.includes(s)),
        },
      }),
    onSuccess: (r) => {
      location.href = r.redirect
    },
  })
  if (q.isLoading) return <Spinner />
  return (
    <PublicFrame>
      <div className="mx-auto max-w-[560px] px-4 py-16">
        <div className="card card-pad md:p-10">
          {!q.data ? (
            <>
              <h1 className="h2 mb-3">{t('consentExpired')}</h1>
              <ErrorNote error={q.error} />
            </>
          ) : (
            <ConsentForm
              d={q.data}
              pick={pick}
              setPick={(i) => {
                setPick(i)
                setOff([])
              }}
              off={off}
              setOff={setOff}
              busy={approve.isPending}
              onApprove={(target) => approve.mutate(target)}
              denyUrl={`${q.data.mcpOrigin}/authorize/deny?handle=${encodeURIComponent(handle)}`}
            />
          )}
          <ErrorNote error={approve.error} />
        </div>
      </div>
    </PublicFrame>
  )
}

function ConsentForm(p: {
  d: Consent
  pick: number
  setPick: (i: number) => void
  off: string[]
  setOff: (s: string[]) => void
  busy: boolean
  onApprove: (t: Target) => void
  denyUrl: string
}) {
  const { t } = useT()
  const target = p.d.targets[p.pick]
  return (
    <div className="grid gap-6">
      <div>
        <div className="eyebrow mb-3">MCP · OAuth</div>
        <h1 className="h2">{t('consentTitle', { client: p.d.client.name })}</h1>
        <p className="mt-2 text-sm text-mute">
          {t('consentRedirect')} <span className="mono">{p.d.client.redirectHost}</span>
        </p>
      </div>
      {!p.d.targets.length ? (
        <p className="text-body">{t('consentNoTargets')}</p>
      ) : (
        <>
          <fieldset className="grid gap-2">
            <legend className="label mb-2">{t('consentAccount')}</legend>
            {p.d.targets.map((x, i) => (
              <label
                key={x.workspaceId ?? 'partner'}
                className="flex items-center gap-3 rounded-lg border border-hairline px-4 py-3"
              >
                <input type="radio" name="target" checked={p.pick === i} onChange={() => p.setPick(i)} />
                <span className="text-ink">{x.name}</span>
                <span className="badge ml-auto">{x.kind === 'partner' ? t('partner') : x.role}</span>
              </label>
            ))}
          </fieldset>
          {target && (
            <fieldset className="grid gap-2">
              <legend className="label mb-2">{t('consentScopes')}</legend>
              {target.scopes.length ? (
                target.scopes.map((s) => (
                  <label key={s} className="flex items-center gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={!p.off.includes(s)}
                      onChange={(e) =>
                        p.setOff(e.target.checked ? p.off.filter((x) => x !== s) : [...p.off, s])
                      }
                    />
                    <span className="mono">{s}</span>
                    <span className="text-mute">
                      {t(`scope_${s.replace('.', '_')}` as 'scope_programs_read')}
                    </span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-body">{t('consentNoScopes')}</p>
              )}
            </fieldset>
          )}
          <p className="text-xs text-mute">{t('consentNote')}</p>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={p.busy || !target || target.scopes.every((s) => p.off.includes(s))}
          onClick={() => target && p.onApprove(target)}
        >
          {t('consentAllow')}
        </button>
        <a className="btn" href={p.denyUrl}>
          {t('consentDeny')}
        </a>
      </div>
    </div>
  )
}
